import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Lock, Unlock, Calendar, FileText, Image as ImgIcon, Mail, Receipt as ReceiptIcon, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * TimelineRuler. horizontal month-by-month ruler. Documents render as tiles
 * positioned at their event_date. The user drags tiles to a new date; we
 * snap to the calendar day under the cursor and PUT the new date to the
 * backend. Documents WITHOUT a date sit in the "Unassigned" tray at the
 * top. drag them onto the ruler to assign.
 *
 * Annex numbers (chronological order) auto-update because they derive from
 * event_date in the existing /annexes / RichText flow.
 */
export default function TimelineRuler({ caseId, docs, onChange, locked, onToggleLock }) {
 const containerRef = useRef(null);
 const [hoverDate, setHoverDate] = useState(null);
 const [savingId, setSavingId] = useState(null);
 const [editing, setEditing] = useState(null); // { doc, draftDate }. tap-to-edit date (mobile)

 // Derive the month range we should draw
 const { months, minMs, msPerDay } = useMemo(() => {
 const dated = docs.filter(d => d.event_date);
 const today = new Date();
 let start, end;
 if (dated.length === 0) {
 start = new Date(today.getFullYear(), today.getMonth() - 5, 1);
 end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
 } else {
 const dates = dated.map(d => new Date(d.event_date.slice(0, 10) + "T00:00:00"));
 const min = new Date(Math.min(...dates));
 const max = new Date(Math.max(...dates));
 start = new Date(min.getFullYear(), min.getMonth() - 1, 1);
 end = new Date(Math.max(max.getTime(), today.getTime()));
 end = new Date(end.getFullYear(), end.getMonth() + 1, 0);
 }
 const months = [];
 let cur = new Date(start);
 while (cur <= end) {
 months.push(new Date(cur));
 cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
 }
 return { months, minMs: start.getTime(), msPerDay: 86400000 };
 }, [docs]);

 // Each month renders at fixed width
 const MONTH_PX = 200;
 const totalPx = months.length * MONTH_PX;
 const daysInRange = months.reduce((acc, m) => acc + new Date(m.getFullYear(), m.getMonth() + 1, 0).getDate(), 0);
 const pxPerDay = totalPx / daysInRange;

 const dateToX = (isoDate) => {
 if (!isoDate) return 0;
 const t = new Date(isoDate.slice(0, 10) + "T00:00:00").getTime();
 const days = Math.max(0, (t - minMs) / msPerDay);
 return days * pxPerDay;
 };

 const xToDate = (x) => {
 const days = Math.max(0, Math.round(x / pxPerDay));
 const d = new Date(minMs + days * msPerDay);
 return d.toISOString().slice(0, 10);
 };

 // ---- DnD ----
 const onDragStart = (e, doc) => {
 if (locked) { e.preventDefault(); return; }
 e.dataTransfer.setData("text/plain", doc.id);
 e.dataTransfer.effectAllowed = "move";
 };

 const onRulerDragOver = (e) => {
 if (locked) return;
 e.preventDefault();
 e.dataTransfer.dropEffect = "move";
 const rect = e.currentTarget.getBoundingClientRect();
 const x = e.clientX - rect.left + (containerRef.current?.scrollLeft || 0);
 setHoverDate(xToDate(x));
 };

 const onRulerDrop = async (e) => {
 e.preventDefault();
 if (locked) return;
 const id = e.dataTransfer.getData("text/plain");
 if (!id) return;
 const rect = e.currentTarget.getBoundingClientRect();
 const x = e.clientX - rect.left + (containerRef.current?.scrollLeft || 0);
 const newDate = xToDate(x);
 setHoverDate(null);
 setSavingId(id);
 try {
 await api.put(`/documents/${id}/metadata`, { event_date: newDate });
 await onChange();
 toast.success("Date updated", { description: `Moved to ${newDate}` });
 } catch (err) {
 toast.error("Couldn't save", { description: err.response?.data?.detail || "Try again" });
 } finally {
 setSavingId(null);
 }
 };

 const onRulerDragLeave = () => setHoverDate(null);

 // Tap-to-edit-date fallback for touch devices (drag-and-drop doesn't fire
 // touch events on mobile). Tile click → modal with native date input.
 const openEdit = (doc) => {
 if (locked) return;
 setEditing({ doc, draftDate: (doc.event_date || new Date().toISOString().slice(0, 10)).slice(0, 10) });
 };
 const closeEdit = () => setEditing(null);
 const saveEdit = async () => {
 if (!editing) return;
 const { doc, draftDate } = editing;
 setSavingId(doc.id);
 try {
 await api.put(`/documents/${doc.id}/metadata`, { event_date: draftDate });
 await onChange();
 setEditing(null);
 toast.success("Date updated", { description: `Moved to ${draftDate}` });
 } catch (err) {
 toast.error("Couldn't save", { description: err.response?.data?.detail || "Try again" });
 } finally {
 setSavingId(null);
 }
 };

 // ---- Scroll helpers ----
 const scroll = (dx) => {
 containerRef.current?.scrollBy({ left: dx, behavior: "smooth" });
 };

 // ---- Lane stacking (avoid overlapping tiles) ----
 const datedDocs = docs.filter(d => d.event_date).sort(
 (a, b) => (a.event_date || "").localeCompare(b.event_date || "")
 );
 const TILE_W = 130;
 const lanes = []; // each: array of rightmost x's at this lane
 const placed = datedDocs.map(d => {
 const x = dateToX(d.event_date);
 let laneIdx = lanes.findIndex(rightX => x >= rightX + 4);
 if (laneIdx === -1) {
 lanes.push(x + TILE_W);
 laneIdx = lanes.length - 1;
 } else {
 lanes[laneIdx] = x + TILE_W;
 }
 return {...d, _x: x, _lane: laneIdx };
 });
 const laneCount = Math.max(1, lanes.length);
 const RULER_HEIGHT = laneCount * 56 + 40; // 40 = month labels

 const undated = docs.filter(d => !d.event_date);

 return (
 <div className="space-y-4" data-testid="timeline-ruler">
 {/* Header */}
 <div className="flex items-center justify-between flex-wrap gap-3">
 <div>
 <div className="label-mono mb-0.5">Sort & lock</div>
 <h3 className="font-serif text-lg text-[#0D2B52]">Drag each tile to its real date</h3>
 <p className="text-xs text-[#6B7280] mt-0.5">When you're happy with the chronology, lock it in. Annex numbers update from left to right.</p>
 </div>
 <button onClick={onToggleLock}
 className={`inline-flex items-center gap-2 text-sm px-3 py-1.5 border ${locked ? "bg-[#1B6C62] text-white border-[#1B6C62] hover:bg-[#1A8478]" : "bg-white text-[#0D2B52] border-[#E5E4DE] hover:border-[#0D2B52]"}`}
 data-testid="lock-toggle">
 {locked ? (<><Lock size={14} strokeWidth={2} /> Locked. Unlock to edit</>) : (<><Unlock size={14} strokeWidth={1.75} /> Lock this order</>)}
 </button>
 </div>

 {/* Undated tray */}
 {undated.length > 0 && (
 <div className="border border-[#E5E4DE] bg-[#FAF7F0] p-3" data-testid="undated-tray">
 <div className="flex items-center gap-2 mb-2">
 <AlertCircle size={14} strokeWidth={1.75} className="text-[#B35D4C]" />
 <span className="label-mono text-[#B35D4C]">{undated.length} document{undated.length === 1 ? "" : "s"} without a date. drag onto the ruler below</span>
 </div>
 <div className="flex gap-2 flex-wrap">
 {undated.map(d => (
 <DocTile key={d.id} doc={d} onDragStart={onDragStart} locked={locked} compact saving={savingId === d.id} />
 ))}
 </div>
 </div>
 )}

 {/* Ruler scroll container */}
 <div className="border border-[#E5E4DE] bg-white relative">
 <div className="absolute top-2 right-2 z-10 flex gap-1">
 <button onClick={() => scroll(-MONTH_PX * 2)} className="bg-white border border-[#E5E4DE] p-1 hover:bg-[#FAF9F6]" data-testid="ruler-scroll-left">
 <ChevronLeft size={14} strokeWidth={1.75} />
 </button>
 <button onClick={() => scroll(MONTH_PX * 2)} className="bg-white border border-[#E5E4DE] p-1 hover:bg-[#FAF9F6]" data-testid="ruler-scroll-right">
 <ChevronRight size={14} strokeWidth={1.75} />
 </button>
 </div>

 <div ref={containerRef} className="overflow-x-auto">
 <div
 className="relative"
 style={{ width: totalPx, height: RULER_HEIGHT }}
 onDragOver={onRulerDragOver}
 onDrop={onRulerDrop}
 onDragLeave={onRulerDragLeave}
 data-testid="ruler-track"
 >
 {/* month columns */}
 {months.map((m, i) => (
 <div key={i}
 className="absolute top-0 bottom-0 border-r border-[#E5E4DE]"
 style={{ left: i * MONTH_PX, width: MONTH_PX }}>
 <div className="bg-[#FAF9F6] px-3 py-2 border-b border-[#E5E4DE] font-mono text-[10px] uppercase tracking-wider text-[#6B7280]">
 {m.toLocaleString("en-AU", { month: "short", year: "numeric" })}
 </div>
 </div>
 ))}

 {/* hover indicator */}
 {hoverDate && !locked && (
 <div className="absolute top-0 bottom-0 w-0.5 bg-[#1B6C62] z-20 pointer-events-none"
 style={{ left: dateToX(hoverDate) }}>
 <div className="absolute -top-1 -translate-x-1/2 left-0 bg-[#1B6C62] text-white text-[10px] px-1.5 py-0.5 font-mono whitespace-nowrap">
 {hoverDate}
 </div>
 </div>
 )}

 {/* tiles */}
 {placed.map(d => (
 <div key={d.id}
 className="absolute"
 style={{ left: d._x, top: 40 + d._lane * 56, width: TILE_W }}>
 <DocTile doc={d} onDragStart={onDragStart} onTap={openEdit} locked={locked} saving={savingId === d.id} />
 </div>
 ))}
 </div>
 </div>
 </div>

 {/* Tap-to-edit-date modal (mobile-friendly fallback for drag-drop) */}
 {editing && (
 <div className="fixed inset-0 z-50 bg-[#0D2B52]/70 flex items-center justify-center p-4" onClick={closeEdit}>
 <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-sm w-full shadow-2xl" data-testid="tile-edit-date-modal">
 <div className="px-5 py-3 border-b border-[#E5E4DE]">
 <div className="label-mono mb-1">Change date</div>
 <div className="font-serif text-base text-[#0D2B52] truncate">{editing.doc.title || editing.doc.original_name}</div>
 </div>
 <div className="px-5 py-4 space-y-3">
 <p className="text-xs text-[#6B7280] font-serif italic">Pick the day this document is dated. Annex numbers will update automatically.</p>
 <input
 type="date"
 className="input-field"
 value={editing.draftDate}
 onChange={(e) => setEditing(prev => prev ? {...prev, draftDate: e.target.value } : prev)}
 data-testid="tile-edit-date-input"
 />
 </div>
 <div className="px-5 py-3 border-t border-[#E5E4DE] flex justify-end gap-2">
 <button onClick={closeEdit} className="btn-ghost text-sm" data-testid="tile-edit-cancel">Cancel</button>
 <button onClick={saveEdit} disabled={!editing.draftDate || savingId === editing.doc.id} className="btn-primary text-sm" data-testid="tile-edit-save">
 {savingId === editing.doc.id ? "Saving…" : "Save"}
 </button>
 </div>
 </div>
 </div>
 )}
 </div>
 );
}

function DocTile({ doc, onDragStart, onTap, locked, compact, saving }) {
 const isImage = (doc.mime_type || "").startsWith("image/");
 const Icon =
 doc.doc_type === "email" ? Mail :
 doc.doc_type === "invoice" ? ReceiptIcon :
 isImage ? ImgIcon : FileText;

 return (
 <div
 draggable={!locked}
 onDragStart={(e) => onDragStart(e, doc)}
 onClick={() => !locked && onTap && onTap(doc)}
 role="button"
 tabIndex={0}
 onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !locked && onTap) { e.preventDefault(); onTap(doc); } }}
 className={`bg-white border border-[#E5E4DE] hover:border-[#0D2B52] p-2 select-none ${locked ? "cursor-default opacity-90" : "cursor-grab active:cursor-grabbing"} ${saving ? "opacity-50" : ""}`}
 title={locked ? doc.original_name : `${doc.original_name}. drag, or tap to change date`}
 data-testid={`doc-tile-${doc.id}`}
 >
 <div className="flex items-center gap-1.5 mb-0.5">
 <Icon size={11} strokeWidth={1.75} className="text-[#1B6C62] flex-shrink-0" />
 <span className="font-mono text-[9px] text-[#6B7280] truncate flex-1">{doc.doc_type || "other"}</span>
 </div>
 <div className="text-[11px] font-medium text-[#1A1A1A] leading-tight" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
 {doc.title || doc.original_name}
 </div>
 {!compact && doc.event_date && (
 <div className="font-mono text-[9px] text-[#6B7280] mt-0.5">{doc.event_date.slice(0, 10)}</div>
 )}
 </div>
 );
}
